import { createForm, type RenderParams } from '@seek/forms-ui';
import { FormActionButtons } from '@seek/profile-edit-ui';
import { useTranslations } from '@vocab/react';
import { Stack, Box } from 'braid-design-system';
import React from 'react';

import type { DirtyFormProps } from 'src/components/FormDrawer/FormDrawer';
import { useOnFormDirtyChange } from 'src/components/FormDrawer/useOnFormDirtyChange';
import { TaxonomyOptionsErrorAlert } from 'src/components/Profile/TaxonomyOptionsErrorAlert/TaxonomyOptionsErrorAlert';
import { WORKTYPES } from 'src/components/Router';
import { useEnterReadMode } from 'src/components/Router/useEnterReadMode';
import { useFocus } from 'src/modules/hooks/useFocus';

import { useUpdateWorkTypes, useWorkTypeOptions, useWorkTypes } from '../data';

import translations from './.vocab';
import { WorkTypeItem } from './WorkTypeItem/WorkTypeItem';

const mapFormStateToProps = ({
  form: { dirty: isFormDirty },
  values: { workTypes },
}: {
  form: {
    dirty: boolean;
  };
  values: { workTypes: number[] };
}) => ({
  isFormDirty,
  workTypes,
});

type MappedProps = ReturnType<typeof mapFormStateToProps>;

interface FormValues {
  workTypes: number[];
}

type FormProps = RenderParams<FormValues> & MappedProps;

const { FormProvider, useField } = createForm<FormValues, MappedProps>(
  'work-types-form',
  {
    formType: 'braid',
  },
);

export const EditWorkTypesForm = ({
  workTypes,
  isFormDirty,
  handleSubmit,
  onFormDirtyChange,
}: FormProps & DirtyFormProps) => {
  const { t } = useTranslations(translations);

  const {
    workTypeOptions,
    loading,
    error: workTypeOptionsError,
    refetch: refetchWorkTypeOptions,
  } = useWorkTypeOptions();

  useOnFormDirtyChange({ onFormDirtyChange, dirty: isFormDirty });

  const enterReadMode = useEnterReadMode();

  const { value: workTypesFieldValues, onChange } = useField({
    id: 'workTypes',
  });

  const workTypesRef = useFocus<HTMLInputElement>({
    card: WORKTYPES,
    fieldName: 'work-types',
    domReady: !loading,
  });

  const [updateWorkTypes] = useUpdateWorkTypes();

  const handleOnSubmit = () => {
    if (isFormDirty) {
      updateWorkTypes({ workTypesData: { ids: workTypesFieldValues } });
    }
    enterReadMode();
  };

  return (
    <>
      {workTypeOptionsError ? (
        <TaxonomyOptionsErrorAlert refetch={refetchWorkTypeOptions} />
      ) : (
        <form onSubmit={handleSubmit(handleOnSubmit)}>
          <Stack space="large">
            <Box role="group" aria-label={t('Preferred work types')}>
              <Stack space="small">
                {workTypeOptions.map((workType, index) => (
                  <WorkTypeItem
                    key={workType.value}
                    workType={workType.value}
                    label={workType.label}
                    isChecked={workTypes.includes(workType.value)}
                    workTypesFieldValues={workTypesFieldValues}
                    onChange={onChange}
                    {...(index === 0 ? { inputRef: workTypesRef } : {})}
                  />
                ))}
              </Stack>
            </Box>
            <FormActionButtons
              cardName={t('Preferred work types')}
              dataAutomationSuffix="work-types"
              onCancel={enterReadMode}
            />
          </Stack>
        </form>
      )}
    </>
  );
};

export const EditWorkTypes = (dirtyFormProps: DirtyFormProps) => {
  const { workTypes } = useWorkTypes();

  return (
    <FormProvider
      mapFormStateToProps={mapFormStateToProps}
      initialValues={{ workTypes: workTypes || [] }}
    >
      {(formProps) => <EditWorkTypesForm {...dirtyFormProps} {...formProps} />}
    </FormProvider>
  );
};
